"use client";

import { useRef, useState, useEffect, useMemo } from "react";
import type { Report, CompatibilityReport } from "@/lib/api";

function splitSections(html: string): string[] {
  return html.split(/(?=<section\b[^>]*>)/);
}

interface Props {
  report: Report | CompatibilityReport;
  onBack: () => void;
}

function LazySection({ html, index }: { html: string; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {visible ? (
        <div dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <div className="h-16 flex items-center justify-center">
          <span className="text-sm text-gray-400">Loading section…</span>
        </div>
      )}
    </div>
  );
}

export default function ReportViewer({ report, onBack }: Props) {
  const sections = useMemo(() => splitSections(report.contentHtml), [report.contentHtml]);

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="text-sm text-indigo-600 hover:underline"
        >
          ← Choose another report
        </button>
        <span className="text-sm text-gray-400">
          {report.interpretationCount} interpretations · {report.sectionCount} sections
          {report.fromCache && " · cached"}
        </span>
      </div>

      <div className="report-content">
        {sections.length > 0 && (
          <div dangerouslySetInnerHTML={{ __html: sections[0] }} />
        )}
        {sections.slice(1).map((html, i) => (
          <LazySection key={i} html={html} index={i} />
        ))}
      </div>

      <style>{`
        .report-content .report-header {
          margin-bottom: 2.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 2px solid #e0e7ff;
        }
        .report-content .report-header h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #1e1b4b;
          margin-bottom: 0.4rem;
        }
        .report-content .report-meta {
          font-size: 0.9rem;
          color: #6b7280;
          font-style: italic;
        }
        .report-content .report-section {
          margin-bottom: 2.5rem;
        }
        .report-content .report-section h2 {
          font-size: 1.35rem;
          font-weight: 700;
          color: #4338ca;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 1.25rem;
          padding-bottom: 0.4rem;
          border-bottom: 1px solid #e0e7ff;
        }
        .report-content .interpretation {
          margin-bottom: 1.75rem;
        }
        .report-content .interpretation-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #312e81;
          margin-bottom: 0.6rem;
        }
        .report-content .interpretation p {
          color: #374151;
          line-height: 1.75;
          margin-bottom: 0.75rem;
        }
        .report-content .interpretation p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
}
